import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AccountInfo, AuthenticationResult, InteractionStatus } from '@azure/msal-browser';
import { Observable, catchError, filter, from, of, switchMap } from 'rxjs';

import { environment } from '../environments/environment';

import { TokenDataStorageService } from './services/token-data-storage.service';

@UntilDestroy()
@Component({
  selector: 'lms-approval-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  constructor(
    private msalBroadcastService: MsalBroadcastService,
    private msalService: MsalService,
    private tokenDataStorageService: TokenDataStorageService,
  ) {}

  ngOnInit(): void {
    this.handleAuthentication();
  }

  private handleAuthentication(): void {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((interactionStatus) => interactionStatus === InteractionStatus.None),
        switchMap(() => from(this.msalService.instance.handleRedirectPromise())),
        switchMap((authResponse) => this.getAuthResponseState(authResponse)),
        filter((silentAuthData) => !!silentAuthData),
        untilDestroyed(this),
      )
      .subscribe((silentAuthData) => {
        if (!silentAuthData) {
          return;
        }

        this.setToken(silentAuthData.accessToken);
        this.tokenDataStorageService.setToken(silentAuthData.accessToken);
      });
  }

  private getAuthResponseState(authResponse: AuthenticationResult | null): Observable<AuthenticationResult | null> {
    const activeUser = this.getActiveAccountState();

    if (activeUser) {
      this.msalService.instance.setActiveAccount(activeUser);
    }

    if (activeUser) {
      return this.msalService.acquireTokenSilent({ scopes: [environment.msalScope] }).pipe(
        catchError(() => {
          this.msalService.loginRedirect();

          return of(null);
        }),
      );
    }

    if (authResponse) {
      this.setToken(authResponse.accessToken);
    }

    return of(null);
  }

  private getActiveAccountState(): AccountInfo | null {
    let activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      activeAccount = this.msalService.instance.getAllAccounts()[0];
    }

    return activeAccount;
  }

  private setToken(toke: string): void {
    this.tokenDataStorageService.setToken(toke);
  }
}
